// apollo-clients.js
import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { gql } from '@apollo/client';
import { fromPromise } from '@apollo/client/link/utils';
import Cookies from 'universal-cookie';

const cookies = new Cookies();
const aToken = process.env.REACT_APP_ACCESS_TOKEN || 'sc-at';
const rToken = process.env.REACT_APP_REFRESH_TOKEN || 'sc-rt';

// Refresh token mutation
const REFRESH_TOKEN_MUTATION = gql`
  mutation RefreshToken($refreshToken: String!) {
    refreshToken(refreshToken: $refreshToken) {
      success
      message
      accessToken
      refreshToken
    }
  }
`;

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_API || 'http://localhost:8085/api',
});

// Auth link to add tokens to requests
const authLink = setContext((_, { headers }) => {
  const token = cookies.get(aToken);
  
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    }
  }
});

// Error link to handle token expiration
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    for (let err of graphQLErrors) {
      if (err.extensions?.code === 401 || err.message === 'INVALID_TOKEN') {
        // Token expired, try to refresh
        const refreshToken = cookies.get(rToken);
        if (refreshToken) {
          // Create a temporary client for token refresh (without auth to avoid infinite loop)
          const refreshClient = new ApolloClient({
            link: createHttpLink({
              uri: process.env.REACT_APP_API || 'http://localhost:8085/api',
            }),
            cache: new InMemoryCache()
          });
          
          return fromPromise(
            refreshClient.mutate({
              mutation: REFRESH_TOKEN_MUTATION,
              variables: { refreshToken }
            }).then((response) => {
              const { data } = response;
              if (data?.refreshToken?.success) {
                // Update cookies with new tokens
                const newAccessToken = data.refreshToken.accessToken;
                const newRefreshToken = data.refreshToken.refreshToken;
                
                cookies.set(aToken, newAccessToken, { path: "/" });
                cookies.set(rToken, newRefreshToken, { path: "/" });

                return true; // Success
              } else {
                cookies.remove(aToken, { path: "/" });
                cookies.remove(rToken, { path: "/" });
                window.location.href = '/login';
                throw new Error('Token refresh failed');
              }
            }).catch((refreshError) => {
              console.error('Token refresh error:', refreshError);
              cookies.remove(aToken, { path: "/" });
              cookies.remove(rToken, { path: "/" });
              window.location.href = '/login';
              throw refreshError;
            })
          ).flatMap(() => forward(operation));
        } else {
          cookies.remove(aToken, { path: "/" });
          window.location.href = '/login';
        }
      }
    }
  }
  
  if (networkError) {
    console.log('Network error:', networkError);
  }
});

// Main client with authentication
export const client = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all'
    },
    query: {
      errorPolicy: 'all'
    }
  }
});

// Auth client without authentication (for login)
export const authClient = new ApolloClient({
  link: from([errorLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all'
    },
    query: {
      errorPolicy: 'all'
    }
  }
});